import { TimeshiftAvatar } from '@/components/shared/TimeshiftAvatar';
import { useGetContractFromRole } from '@/services/contract/useGetContractFromRole';
import { useContract } from '@/services/contract/useContract';
import { findContractById } from '@/services/contract/contractHelper';
import { useContracts } from '@/services/contract/useContracts';
import { DailyEditingType, WorkingShift } from '@/types/daily.types';
import { EmployeeForShiftItem } from '@/types/employeeForShift.types';
import { EMPLOYEE_NAME_COLUMN_WIDTH } from '@/helpers/constants';
import { FC } from 'react';
import { FiChevronDown, FiX } from 'react-icons/fi';
import { Popover } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { dropdownId, getDropdownSettings, shiftsWrapperId } from '../roster/staffPicker/rosterPickerHelper';
import { useEffect } from 'react';
import { merge } from '@/helpers/Utility';
import { ShiftTimePicker } from '../roster/ShiftTimePicker';
import { useTemplateEmployeesForShift } from './useTemplateEmployees';
import { TemplateShiftLock } from './TemplateShiftLock';

const TemplatePickerPanel: FC<{
  options: EmployeeForShiftItem[];
  value?: EmployeeForShiftItem;
  onChange: (employee?: EmployeeForShiftItem) => void;
  close: () => void;
}> = ({ options, value, onChange, close }) => {
  useEffect(() => {
    const wrapper = document.querySelector(`#${shiftsWrapperId}`);
    wrapper?.addEventListener('scroll', close);

    return () => wrapper?.removeEventListener('scroll', close);
  }, [close]);

  return (
    <>
      <div className="border-b p-2 font-semibold">Select staff</div>
      {options.map(option => {
        const selected = value?.id === option.id;
        return (
          <button
            key={option.id}
            className={merge('border-b flex gap-3 p-2 items-center w-full text-left', selected ? 'bg-primary-200' : '')}
            onClick={() => {
              onChange(selected ? undefined : option);
              close();
            }}
          >
            <TimeshiftAvatar name={option.name} src={option.details?.avatarUrl} size={30} />
            <div className="flex-1 truncate">
              <div className="font-semibold">{option.name}</div>
              <div className="text-sm text-secondary-std">
                {option.details?.employeeId ? `(${option.details.employeeId})` : ''}
                {option.contract ? `${option.details?.employeeId ? ' · ' : ''}${option.contract}` : ''}
              </div>
            </div>
          </button>
        );
      })}
    </>
  );
};

const TemplatePicker: FC<{
  shiftId: string;
  options: EmployeeForShiftItem[];
  value?: EmployeeForShiftItem;
  onChange: (employee?: EmployeeForShiftItem) => void;
}> = ({ shiftId, options, value, onChange }) => {
  return (
    <Popover className="flex-1">
      {({ open }) => (
        <>
          <Popover.Button
            className={merge(
              'w-full flex items-center py-1 pl-3 relative cursor-default overflow-hidden rounded-lg border gap-2',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75'
            )}
            id={dropdownId(shiftId)}
          >
            {value?.name ? <TimeshiftAvatar name={value?.name} src={value?.details?.avatarUrl} size={30} /> : undefined}
            <div className="flex-1 w-full border-none text-sm leading-5 focus:ring-0 text-ellipsis text-left">
              {value?.name || 'Not assigned'}
            </div>
            <FiChevronDown className="h-9 w-9" />
          </Popover.Button>
          {!open
            ? null
            : createPortal(
                <Popover.Panel
                  style={getDropdownSettings(shiftId, options)}
                  className="flex flex-col absolute z-[1000] bg-white border overflow-auto w-[400px] rounded-md"
                >
                  {({ close }) => (
                    <TemplatePickerPanel close={close} value={value} options={options} onChange={onChange} />
                  )}
                </Popover.Panel>,
                document.body
              )}
        </>
      )}
    </Popover>
  );
};

interface Props {
  shift: WorkingShift;
  updateShift: (newShift: WorkingShift) => void;
  removeShift: () => void;
}

export const TemplateShift: FC<Props> = ({ shift, updateShift, removeShift }) => {
  const getContract = useGetContractFromRole();
  const contractId = shift.assignedContractId
    ? shift.assignedContractId
    : getContract(shift.assignedContractRoleId ?? '')?.id;
  const contract = useContract(contractId);
  const isLocked = !!shift.properties?.staffLocked;
  const hasAssignedEmployee = !!shift.assignedEmployeeId;

  const employeeOptions = useTemplateEmployeesForShift(shift);
  const employee = employeeOptions.find(option => option.id === shift.assignedEmployeeId);
  const { contracts } = useContracts();
  const employeeContract = findContractById(contracts, employee?.contractId);

  const onSelectStaff = (newStaff?: EmployeeForShiftItem) => {
    const nextProperties = { ...(shift.properties || {}) };
    if (!newStaff?.id) {
      delete nextProperties.staffLocked;
    }

    updateShift({
      ...shift,
      assignedEmployeeId: newStaff?.id,
      properties: nextProperties
    });
  };

  const onToggleLock = () => {
    if (!hasAssignedEmployee) {
      return;
    }

    updateShift({
      ...shift,
      properties: {
        ...(shift.properties || {}),
        staffLocked: !isLocked
      }
    });
  };

  return (
    <div className="flex flex-row border-b">
      <div className="flex sticky left-0 z-[60] bg-white">
        <div
          className="flex items-center px-2 font-semibold gap-3 border-r"
          style={{ width: EMPLOYEE_NAME_COLUMN_WIDTH }}
        >
          <button onClick={removeShift}>
            <FiX className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0 py-2 flex space-x-2">
            <TemplatePicker shiftId={shift.id} options={employeeOptions} onChange={onSelectStaff} value={employee} />
            {hasAssignedEmployee && <TemplateShiftLock isLocked={isLocked} onToggleLock={onToggleLock} />}
          </div>
        </div>
      </div>
      <div className="flex-1">
        <ShiftTimePicker
          key={shift.id}
          shift={shift}
          updateShift={updateShift}
          type={DailyEditingType.TEMPLATE}
          useColorizedBar
          hasSetEmployee={hasAssignedEmployee}
          contract={contract}
          employeeContract={employeeContract}
        />
      </div>
    </div>
  );
};
